import React, { useEffect, useMemo, useState } from 'react';
import { usePortalContext } from '../context/PortalContext';
import {
  SHAREPOINT_ALM_DOCS_URL,
  SHAREPOINT_SITE_COLLECTION_APP_CATALOG_DOCS_URL
} from '../services/sharepoint/spfxAppCatalogDeploymentDocs';
import {
  acquireGraphFeatureTokenInteractively,
  acquireGraphFeatureTokenSilently
} from '../services/graph/interactiveGraphFeatureClient';

type SpfxReleaseRecord = {
  releaseTag: string;
  releaseName: string;
  releaseChannel: string;
  version: string;
  solutionName?: string;
  solutionId?: string;
  fileName: string;
  checksumSha256: string;
  generatedAtUtc: string;
  publishedAtUtc?: string;
  supportsTeamsChoices?: string[];
  teamsPackagingMode?: 'none' | 'sharepoint_generated' | 'developer_provided';
  isRecommended?: boolean;
};

type SpfxReleaseIndex = {
  recommendedReleaseTag?: string;
  releases: SpfxReleaseRecord[];
};

type InstallScope = 'tenant' | 'sitecollection';

type InstallTargetOption = {
  url: string;
  label: string;
  source: 'tenant_root_known' | 'tenant_root_inferred' | 'known_instance' | 'graph_search';
  description?: string;
  instanceId?: string;
  pageUrl?: string;
  siteCollectionAppCatalogStatus?: 'enabled' | 'not_enabled' | 'unknown';
};

type InstallTargetDiscovery = {
  defaultTargetUrl?: string;
  graphSiteDiscoveryActive: boolean;
  tenantRootCandidates: InstallTargetOption[];
  knownSites: InstallTargetOption[];
  searchedSites: InstallTargetOption[];
};

type GraphSiteDiscoveryState = {
  available: boolean;
  active: boolean;
  clientId?: string;
  tokenHeaderName: string;
  permissions: string[];
  permissionMayRequireItApproval: boolean;
};

type DeployableTenant = {
  tenantId: string;
  displayName: string;
  isBillingTenant: boolean;
  isSessionTenant: boolean;
  sharePointHost?: string;
};

type DeployableTenantResponse = {
  tenants: DeployableTenant[];
  defaultTenantId: string;
};

type InstallResult = {
  operationId: string;
  status: 'running' | 'succeeded' | 'failed';
  stage:
    | 'queued'
    | 'acquiring_token'
    | 'resolving_release'
    | 'resolving_app_catalog'
    | 'uploading_package'
    | 'waiting_for_available_app'
    | 'deploying_package'
    | 'syncing_to_teams'
    | 'completed';
  statusMessage: string | null;
  correlationId: string | null;
};

type DeploymentOperation = {
  operationId: string;
  tenantId: string;
  releaseTag: string;
  releaseVersion: string | null;
  targetScope: InstallScope;
  targetUrl: string;
  tenantRootUrl: string | null;
  appCatalogUrl: string | null;
  appId: string | null;
  status: 'running' | 'succeeded' | 'failed';
  stage:
    | 'queued'
    | 'acquiring_token'
    | 'resolving_release'
    | 'resolving_app_catalog'
    | 'uploading_package'
    | 'waiting_for_available_app'
    | 'deploying_package'
    | 'syncing_to_teams'
    | 'completed';
  statusMessage: string | null;
  lastError: string | null;
  correlationId: string | null;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
};

type ReleaseInstanceView = {
  instanceId: string;
  siteUrl: string;
  pageUrl: string;
  webPartVersion: string;
  lastSeenAt: string;
  status: 'active' | 'recent' | 'stale' | 'superseded' | 'deregistered';
  isOutdated: boolean;
  attention?: {
    runtimeGate: string;
    installerState: string | null;
    reason: string | null;
    notificationSummary: string | null;
  } | null;
  maintenance?: {
    requestedAction: 'install' | 'repair' | 'upgrade';
    status: 'pending' | 'running' | 'succeeded' | 'failed' | 'cancelled';
    stage: string;
    statusMessage: string | null;
  } | null;
};

type ReleaseInstanceSummary = {
  totalActive: number;
  totalSuperseded?: number;
  needsUpdate: number;
  needsAttention: number;
  maintenanceRunning: number;
};

type ReleaseInstanceSiteGroup = {
  siteUrl: string;
  rows: ReleaseInstanceView[];
};

const RELEASE_INSTANCE_STATUS_LABEL: Record<ReleaseInstanceView['status'], string> = {
  active: 'active',
  recent: 'recent',
  stale: 'stale heartbeat',
  superseded: 'superseded',
  deregistered: 'deregistered'
};

const describePagePath = (pageUrl: string, siteUrl: string): string => {
  const trimmed = String(pageUrl ?? '').trim();
  if (!trimmed) {
    return '(page not reported)';
  }
  try {
    const page = new URL(trimmed);
    const site = new URL(siteUrl);
    const sitePath = site.pathname.replace(/\/+$/, '');
    const relative = page.pathname.startsWith(sitePath) ? page.pathname.slice(sitePath.length) : page.pathname;
    return decodeURIComponent(relative || '/');
  } catch {
    return trimmed;
  }
};

const relativeTimeLabel = (iso: string): string => {
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) {
    return 'just now';
  }
  const minutes = Math.floor(ms / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours} h ago`;
  return `${Math.floor(hours / 24)} days ago`;
};

/**
 * Groups the rollout rows by site so an operator sees "one site, two web part
 * placements" instead of two indistinguishable entries. Only rows that still
 * need something (upgrade, attention, maintenance state) are listed; superseded
 * and deregistered instances are counted but not listed.
 */
const groupReleaseInstancesBySite = (rows: ReleaseInstanceView[]): ReleaseInstanceSiteGroup[] => {
  const groups = new Map<string, ReleaseInstanceSiteGroup>();
  for (const row of rows) {
    const key = String(row.siteUrl ?? '').trim().replace(/\/+$/, '').toLowerCase();
    const group = groups.get(key);
    if (group) {
      group.rows.push(row);
    } else {
      groups.set(key, { siteUrl: row.siteUrl, rows: [row] });
    }
  }
  return Array.from(groups.values());
};

const stageOrder: DeploymentOperation['stage'][] = [
  'queued',
  'acquiring_token',
  'resolving_release',
  'resolving_app_catalog',
  'uploading_package',
  'waiting_for_available_app',
  'deploying_package',
  'syncing_to_teams',
  'completed'
];

const formatDateTime = (isoDate: string | null | undefined): string => {
  if (!isoDate) {
    return 'Unknown';
  }
  const date = new Date(isoDate);
  if (Number.isNaN(date.valueOf())) {
    return isoDate;
  }
  return date.toLocaleString();
};

const describeStage = (stage: DeploymentOperation['stage']): string => {
  switch (stage) {
    case 'queued':
      return 'Queued';
    case 'acquiring_token':
      return 'Acquiring token';
    case 'resolving_release':
      return 'Resolving release';
    case 'resolving_app_catalog':
      return 'Resolving app catalog';
    case 'uploading_package':
      return 'Uploading package';
    case 'waiting_for_available_app':
      return 'Waiting for AvailableApps';
    case 'deploying_package':
      return 'Deploying package';
    case 'syncing_to_teams':
      return 'Syncing to Teams';
    case 'completed':
      return 'Completed';
    default:
      return stage;
  }
};

const describeInstallTargetSource = (source: InstallTargetOption['source']): string => {
  switch (source) {
    case 'tenant_root_known':
      return 'Known tenant root';
    case 'tenant_root_inferred':
      return 'Inferred tenant root';
    case 'known_instance':
      return 'Known Edinshall site';
    case 'graph_search':
      return 'Microsoft 365 discovery';
    default:
      return 'Suggested target';
  }
};

const describeScope = (scope: InstallScope): string =>
  scope === 'tenant' ? 'Tenant app catalog' : 'Site collection app catalog';

const describeTeamsChoice = (choice: string): string => {
  switch (choice) {
    case 'teams_tab_only':
      return 'tab only';
    case 'teams_personal_app_and_tab':
      return 'legacy personal app + tab (not eligible for Teams sync)';
    default:
      return choice;
  }
};

const describeTeamsPackagingMode = (mode: SpfxReleaseRecord['teamsPackagingMode']): string | null => {
  switch (mode) {
    case 'developer_provided':
      return 'custom Teams package';
    case 'sharepoint_generated':
      return 'SharePoint-generated Teams package';
    default:
      return null;
  }
};

const formatElapsed = (startedAt: string | null | undefined, now: number): string => {
  if (!startedAt) {
    return '0s';
  }
  const parsed = Date.parse(startedAt);
  if (Number.isNaN(parsed)) {
    return '0s';
  }
  const totalSeconds = Math.max(0, Math.floor((now - parsed) / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) {
    return `${hours}h ${minutes}m ${seconds}s`;
  }
  if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  }
  return `${seconds}s`;
};

const SpfxReleasesAdmin = (): JSX.Element => {
  const { tenant } = usePortalContext();
  const canManage = tenant?.capabilities?.canDeploySpfx === true;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [index, setIndex] = useState<SpfxReleaseIndex | null>(null);
  const [selectedTag, setSelectedTag] = useState('');
  const [deployableTenants, setDeployableTenants] = useState<DeployableTenant[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [targetScope, setTargetScope] = useState<InstallScope>('tenant');
  const [syncToTeams, setSyncToTeams] = useState(false);
  const [targetUrl, setTargetUrl] = useState('');
  const [targetDiscovery, setTargetDiscovery] = useState<InstallTargetDiscovery | null>(null);
  const [targetDiscoveryError, setTargetDiscoveryError] = useState<string | null>(null);
  const [graphDiscovery, setGraphDiscovery] = useState<GraphSiteDiscoveryState | null>(null);
  const [graphDiscoveryUpdating, setGraphDiscoveryUpdating] = useState(false);
  const [siteSearch, setSiteSearch] = useState('');
  const [searchingSites, setSearchingSites] = useState(false);
  const [graphPermissionRequired, setGraphPermissionRequired] = useState(false);
  const [graphPermissionError, setGraphPermissionError] = useState<string | null>(null);
  const [graphItRequestCopied, setGraphItRequestCopied] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [installError, setInstallError] = useState<string | null>(null);
  const [installResult, setInstallResult] = useState<InstallResult | null>(null);
  const [operations, setOperations] = useState<DeploymentOperation[]>([]);
  const [activeOperationId, setActiveOperationId] = useState<string | null>(null);
  const [expandedOperations, setExpandedOperations] = useState<Record<string, boolean>>({});
  const [now, setNow] = useState(() => Date.now());
  const [dismissingOperationId, setDismissingOperationId] = useState<string | null>(null);
  const [instanceSummary, setInstanceSummary] = useState<ReleaseInstanceSummary | null>(null);
  const [instanceCurrentVersion, setInstanceCurrentVersion] = useState<{ version: string; source: string } | null>(null);
  const [instanceRows, setInstanceRows] = useState<ReleaseInstanceView[]>([]);

  const loadOperations = async (): Promise<void> => {
    try {
      const response = await fetch('/api/admin/spfx-releases/operations');
      if (!response.ok) {
        return;
      }
      const payload = (await response.json()) as { operations?: DeploymentOperation[] };
      setOperations(payload.operations ?? []);
    } catch {
      // best-effort only
    }
  };

  const loadInstanceRollout = async (tenantId: string): Promise<void> => {
    if (!tenantId) {
      setInstanceSummary(null);
      setInstanceCurrentVersion(null);
      setInstanceRows([]);
      return;
    }
    try {
      const params = new URLSearchParams({ tenantId });
      const response = await fetch(`/api/instances?${params.toString()}`);
      if (!response.ok) {
        return;
      }
      const payload = (await response.json()) as {
        summary?: ReleaseInstanceSummary;
        instances?: ReleaseInstanceView[];
        currentVersion?: string;
        currentVersionSource?: string;
      };
      setInstanceSummary(payload.summary ?? null);
      setInstanceCurrentVersion(
        payload.currentVersion ? { version: payload.currentVersion, source: payload.currentVersionSource ?? '' } : null
      );
      const rows = Array.isArray(payload.instances) ? payload.instances : [];
      setInstanceRows(
        rows
          .filter((entry) => entry.status !== 'superseded' && entry.status !== 'deregistered')
          .filter((entry) => entry.isOutdated || entry.attention || entry.maintenance?.status === 'running' || entry.maintenance?.status === 'pending' || entry.maintenance?.status === 'failed')
          .slice(0, 12)
      );
    } catch {
      setInstanceSummary(null);
      setInstanceRows([]);
    }
  };

  const loadGraphDiscoveryState = async (tenantId: string): Promise<void> => {
    const params = new URLSearchParams({ tenantId });
    const response = await fetch(`/api/admin/spfx-releases/site-discovery?${params.toString()}`);
    if (!response.ok) {
      const payload = await response.json().catch(() => ({})) as { message?: string };
      throw new Error(payload.message ?? `Site-discovery settings failed (${response.status})`);
    }
    setGraphDiscovery(await response.json() as GraphSiteDiscoveryState);
  };

  const loadTargets = async (tenantId: string, query = '', graphToken = ''): Promise<void> => {
    if (!tenantId) {
      return;
    }

    setTargetDiscoveryError(null);
    if (query) setSearchingSites(true);
    try {
      const params = new URLSearchParams();
      params.set('tenantId', tenantId);
      if (query) params.set('q', query);
      const response = await fetch(`/api/admin/spfx-releases/install-targets?${params.toString()}`, {
        headers: graphToken && graphDiscovery?.tokenHeaderName
          ? { [graphDiscovery.tokenHeaderName]: graphToken }
          : undefined
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({})) as { message?: string };
        throw new Error(payload.message ?? `Install target discovery failed (${response.status})`);
      }
      const payload = (await response.json()) as InstallTargetDiscovery;
      setTargetDiscovery(payload);
      setTargetUrl((current) => current.trim() || payload.defaultTargetUrl || '');
    } catch (err) {
      setTargetDiscoveryError((err as Error).message);
    } finally {
      setSearchingSites(false);
    }
  };

  const setGraphDiscoveryActive = async (active: boolean): Promise<void> => {
    if (!selectedTenantId) return;
    setGraphDiscoveryUpdating(true);
    setGraphPermissionError(null);
    try {
      const response = await fetch('/api/admin/spfx-releases/site-discovery', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenantId: selectedTenantId, active })
      });
      const payload = await response.json().catch(() => ({})) as { message?: string };
      if (!response.ok) throw new Error(payload.message ?? `Update failed (${response.status})`);
      await loadGraphDiscoveryState(selectedTenantId);
      if (!active) {
        setGraphPermissionRequired(false);
        setSiteSearch('');
        await loadTargets(selectedTenantId);
      }
    } catch (err) {
      setGraphPermissionError((err as Error).message);
    } finally {
      setGraphDiscoveryUpdating(false);
    }
  };

  const runGraphSiteSearch = async (interactive: boolean): Promise<void> => {
    if (!graphDiscovery?.active || !graphDiscovery.clientId || siteSearch.trim().length < 2) return;
    setGraphPermissionError(null);
    const scopes = graphDiscovery.permissions.map(permission => `https://graph.microsoft.com/${permission}`);
    try {
      const accessToken = interactive
        ? await acquireGraphFeatureTokenInteractively({
            clientId: graphDiscovery.clientId,
            tenantId: selectedTenantId,
            scopes
          })
        : await acquireGraphFeatureTokenSilently({
            clientId: graphDiscovery.clientId,
            tenantId: selectedTenantId,
            scopes,
            loginHint: tenant?.userEmail
          });
      if (!accessToken) {
        setGraphPermissionRequired(true);
        return;
      }
      setGraphPermissionRequired(false);
      await loadTargets(selectedTenantId, siteSearch.trim(), accessToken);
    } catch (err) {
      setGraphPermissionError((err as Error).message);
      setGraphPermissionRequired(true);
    }
  };

  const copyGraphPermissionRequest = async (): Promise<void> => {
    if (!graphDiscovery?.clientId) return;
    const request = [
      'Please approve optional Microsoft 365 site discovery for Edinshall Audit.',
      `Tenant: ${selectedTenantId}`,
      `Application client ID: ${graphDiscovery.clientId}`,
      'Delegated Microsoft Graph permission: Sites.Read.All',
      'Purpose: allow an authorised Portal administrator to search SharePoint sites before selecting an SPFx deployment target.',
      'The feature performs no Graph writes, is off by default for the tenant, and known-site/manual URL deployment remains available without it.'
    ].join('\n');
    try {
      await navigator.clipboard.writeText(request);
      setGraphItRequestCopied(true);
    } catch {
      setGraphPermissionError('The IT permission request could not be copied. Copy the tenant, application client ID, and Sites.Read.All permission shown here manually.');
    }
  };

  const load = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const [releaseResponse, tenantResponse] = await Promise.all([
        fetch('/api/admin/spfx-releases'),
        fetch('/api/admin/spfx-releases/deployable-tenants')
      ]);

      if (!releaseResponse.ok) {
        const text = await releaseResponse.text();
        throw new Error(text || `Release archive request failed (${releaseResponse.status})`);
      }
      if (!tenantResponse.ok) {
        const text = await tenantResponse.text();
        throw new Error(text || `Deployable tenants request failed (${tenantResponse.status})`);
      }

      const releasePayload = (await releaseResponse.json()) as SpfxReleaseIndex;
      const tenantPayload = (await tenantResponse.json()) as DeployableTenantResponse;

      setIndex(releasePayload);
      setSelectedTag(releasePayload.recommendedReleaseTag || releasePayload.releases[0]?.releaseTag || '');
      setDeployableTenants(tenantPayload.tenants ?? []);
      setSelectedTenantId(tenantPayload.defaultTenantId || tenantPayload.tenants[0]?.tenantId || '');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!canManage) {
      setLoading(false);
      return;
    }
    void load();
    void loadOperations();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManage]);

  useEffect(() => {
    if (!canManage || !selectedTenantId) {
      return;
    }
    setTargetDiscovery(null);
    setTargetDiscoveryError(null);
    setTargetUrl('');
    setSyncToTeams(false);
    setShowAdvanced(false);
    setSiteSearch('');
    setGraphPermissionRequired(false);
    setGraphPermissionError(null);
    setGraphItRequestCopied(false);
    void loadTargets(selectedTenantId);
    void loadGraphDiscoveryState(selectedTenantId).catch(err => setGraphPermissionError((err as Error).message));
    void loadInstanceRollout(selectedTenantId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManage, selectedTenantId]);

  useEffect(() => {
    if (!targetDiscovery) {
      return;
    }

    const preferredOptions =
      targetScope === 'tenant'
        ? [...targetDiscovery.tenantRootCandidates, ...targetDiscovery.knownSites, ...targetDiscovery.searchedSites]
        : [...targetDiscovery.knownSites, ...targetDiscovery.searchedSites]
            .filter((entry) => entry.siteCollectionAppCatalogStatus === 'enabled');

    setTargetUrl((current) => {
      const trimmed = current.trim();
      if (trimmed && (targetScope === 'tenant' || preferredOptions.some((entry) => entry.url === trimmed))) {
        return current;
      }
      return preferredOptions[0]?.url || (targetScope === 'tenant' ? targetDiscovery.defaultTargetUrl || '' : '');
    });
  }, [targetDiscovery, targetScope]);

  const suggestedTargets = useMemo(() => {
    if (!targetDiscovery) {
      return [];
    }
    return targetScope === 'tenant'
      ? [...targetDiscovery.tenantRootCandidates, ...targetDiscovery.knownSites, ...targetDiscovery.searchedSites]
      : [...targetDiscovery.knownSites, ...targetDiscovery.searchedSites]
          .filter((entry) => entry.siteCollectionAppCatalogStatus === 'enabled');
  }, [targetDiscovery, targetScope]);

  const siteCollectionCandidateCount = useMemo(() => {
    if (!targetDiscovery) {
      return 0;
    }
    return targetDiscovery.knownSites.length + targetDiscovery.searchedSites.length;
  }, [targetDiscovery]);

  useEffect(() => {
    if (targetScope === 'sitecollection' && suggestedTargets.length === 0) {
      setShowAdvanced(true);
    }
  }, [targetScope, suggestedTargets.length]);

  useEffect(() => {
    if (!canManage || !activeOperationId) {
      return undefined;
    }
    const timer = window.setInterval(() => {
      setNow(Date.now());
      void loadOperations();
    }, 3000);
    return () => window.clearInterval(timer);
  }, [canManage, activeOperationId]);

  useEffect(() => {
    if (!activeOperationId) {
      return undefined;
    }
    const timer = window.setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return () => window.clearInterval(timer);
  }, [activeOperationId]);

  useEffect(() => {
    if (!activeOperationId) {
      return;
    }
    const operation = operations.find((entry) => entry.operationId === activeOperationId);
    if (!operation || operation.status === 'running') {
      return;
    }

    setInstalling(false);
    setActiveOperationId(null);
    if (operation.status === 'succeeded') {
      setInstallError(null);
      setInstallResult(null);
    } else if (operation.lastError) {
      setInstallError(operation.lastError);
    }
  }, [activeOperationId, operations]);

  useEffect(() => {
    setExpandedOperations((current) => {
      const next = { ...current };
      let changed = false;
      operations.forEach((operation) => {
        if (operation.status === 'running' && !next[operation.operationId]) {
          next[operation.operationId] = true;
          changed = true;
        }
      });
      return changed ? next : current;
    });
  }, [operations]);

  const selectedRelease = useMemo(
    () => index?.releases.find((release) => release.releaseTag === selectedTag) ?? null,
    [index, selectedTag]
  );

  const selectedTenant = useMemo(
    () => deployableTenants.find((entry) => entry.tenantId === selectedTenantId) ?? null,
    [deployableTenants, selectedTenantId]
  );
  const selectedReleaseSupportsTeams = selectedRelease?.supportsTeamsChoices?.includes('teams_tab_only') === true;
  const selectedReleaseIsRollbackCandidate = Boolean(
    selectedRelease && index?.recommendedReleaseTag && selectedRelease.releaseTag !== index.recommendedReleaseTag
  );

  const tenantLookup = useMemo(() => {
    const map = new Map<string, DeployableTenant>();
    deployableTenants.forEach((entry) => map.set(entry.tenantId, entry));
    return map;
  }, [deployableTenants]);

  const activeOperation = useMemo(
    () => operations.find((entry) => entry.operationId === activeOperationId) ?? null,
    [operations, activeOperationId]
  );

  const installTargetLabel = targetScope === 'tenant' ? 'SharePoint tenant or site URL' : 'Site collection URL';
  const installTargetHint =
    targetScope === 'tenant'
      ? 'The selected tenant is the default target. You can still override with any SharePoint site URL in that tenant if needed.'
      : 'Enter the site collection URL that hosts the site collection app catalog.';
  const tenantDefaultTargetUrl =
    (targetUrl.trim() && targetScope === 'tenant'
      ? targetUrl.trim()
      : selectedTenant?.sharePointHost
        ? `https://${selectedTenant.sharePointHost}`
        : targetDiscovery?.tenantRootCandidates[0]?.url || targetDiscovery?.defaultTargetUrl || ''
    ).trim();
  const effectiveDeployTargetUrl = targetScope === 'tenant' ? tenantDefaultTargetUrl : targetUrl.trim();

  const activeProgressPercent = activeOperation
    ? Math.max(8, Math.round(((stageOrder.indexOf(activeOperation.stage) + 1) / stageOrder.length) * 100))
    : 0;

  const toggleOperation = (operationId: string): void => {
    setExpandedOperations((current) => ({
      ...current,
      [operationId]: !current[operationId]
    }));
  };

  const dismissOperation = async (operationId: string): Promise<void> => {
    setDismissingOperationId(operationId);
    try {
      const response = await fetch(`/api/admin/spfx-releases/operations/${encodeURIComponent(operationId)}/dismiss`, {
        method: 'POST'
      });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || `Dismiss failed (${response.status})`);
      }
      setExpandedOperations((current) => {
        const next = { ...current };
        delete next[operationId];
        return next;
      });
      await loadOperations();
    } catch (err) {
      setInstallError((err as Error).message);
    } finally {
      setDismissingOperationId(null);
    }
  };

  const install = async (): Promise<void> => {
    if (!selectedRelease || !selectedTenantId || !effectiveDeployTargetUrl) {
      return;
    }
    if (
      selectedReleaseIsRollbackCandidate &&
      !window.confirm(
        `Deploy approved release ${selectedRelease.version} instead of the recommended release? If the target currently has a newer package, this is a rollback. The deployment history will retain the selected release and validation result.`
      )
    ) {
      return;
    }

    setInstalling(true);
    setInstallError(null);
    setInstallResult(null);
    setActiveOperationId(null);

    try {
      const response = await fetch(`/api/admin/spfx-releases/${encodeURIComponent(selectedRelease.releaseTag)}/install`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenantId: selectedTenantId,
          targetScope,
          targetUrl: effectiveDeployTargetUrl,
          syncToTeams: targetScope === 'tenant' ? syncToTeams : false
        })
      });
      const payloadText = await response.text();
      if (!response.ok) {
        throw new Error(payloadText || `SPFx install failed (${response.status})`);
      }
      const payload = JSON.parse(payloadText) as InstallResult;
      setInstallResult(payload);
      setActiveOperationId(payload.operationId);
      setExpandedOperations((current) => ({ ...current, [payload.operationId]: true }));
      void loadOperations();
    } catch (err) {
      setInstallError((err as Error).message);
      setInstalling(false);
      void loadOperations();
    }
  };

  if (!canManage) {
    return (
      <div className="card">
        <h1 className="section-title">SPFx Releases</h1>
        <p className="muted">You do not have permission to manage SPFx release artifacts.</p>
      </div>
    );
  }

  return (
    <div>
      <h1 className="section-title">SPFx Releases</h1>
      <p className="muted">
        Deploy an approved SPFx package to a licensed SharePoint app catalog, and optionally sync Teams-capable packages into the tenant Teams app catalog.
      </p>
      <div className="card" style={{ marginTop: '12px' }}>
        <p className="muted" style={{ margin: 0 }}>
          This deployment flow uses your existing Microsoft permissions in the target tenant/site.
          The customer must ensure the operator is already permissioned (typically a customer system
          administrator with Global Admin or equivalent delegated app-catalog rights). Edinshall Audit
          does not invent or grant Microsoft permissions.
        </p>
      </div>

      {loading && <div className="badge-subtle" style={{ marginTop: '12px' }}>Loading SPFx release data…</div>}
      {error && <div className="pill warning" style={{ marginTop: '12px' }}>{error}</div>}

      {!loading && !error && index && index.releases.length === 0 && (
        <div className="card" style={{ marginTop: '12px' }}>
          <div className="pill warning">No approved SPFx releases have been published yet.</div>
        </div>
      )}

      {!loading && !error && index && index.releases.length > 0 && selectedRelease && (
        <div style={{ display: 'grid', gap: '16px', marginTop: '12px' }}>
          <div className="card">
            <h3>Deploy to App Catalog</h3>
            <p className="muted">
              Use delegated admin consent to deploy the selected package to a tenant app catalog or a site collection app catalog. Teams sync is optional and only applies to tenant app catalog deployments for Teams-capable packages.
            </p>

            <div style={{ display: 'grid', gap: '12px', marginTop: '14px' }}>
              <label>
                Tenant
                {deployableTenants.length <= 1 && selectedTenant ? (
                  <div style={{ marginTop: '6px', border: '1px solid #e2e8f0', borderRadius: '4px', padding: '10px 12px', background: '#f8fafc' }}>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                      <strong>{selectedTenant.displayName}</strong>
                      {selectedTenant.sharePointHost && <span className="badge-subtle">{selectedTenant.sharePointHost}</span>}
                      {selectedTenant.isBillingTenant && <span className="badge-subtle">Billing tenant</span>}
                      {selectedTenant.isSessionTenant && <span className="badge-subtle">Signed-in tenant</span>}
                    </div>
                  </div>
                ) : (
                  <select
                    className="input"
                    value={selectedTenantId}
                    onChange={(event) => setSelectedTenantId(event.target.value)}
                    style={{ marginTop: '6px' }}
                    disabled={installing}
                  >
                    {deployableTenants.map((entry) => (
                      <option key={entry.tenantId} value={entry.tenantId}>
                        {entry.displayName}
                        {entry.sharePointHost ? ` (${entry.sharePointHost})` : ''}
                        {entry.isBillingTenant ? ' [billing]' : ''}
                        {entry.isSessionTenant ? ' [signed-in]' : ''}
                      </option>
                    ))}
                  </select>
                )}
              </label>

              <label>
                Release
                <select
                  className="input"
                  value={selectedTag}
                  onChange={(event) => setSelectedTag(event.target.value)}
                  style={{ marginTop: '6px' }}
                  disabled={installing}
                >
                  {index.releases.map((release) => (
                    <option key={release.releaseTag} value={release.releaseTag}>
                      {release.version}{release.isRecommended ? ' (recommended)' : ''} — {release.releaseChannel}
                    </option>
                  ))}
                </select>
              </label>

              {selectedReleaseIsRollbackCandidate && (
                <div className="pill warning">
                  You selected a non-recommended approved release. Deploying it over a newer package is an explicit rollback; the operation history records the release, target, actor and SharePoint validation result.
                </div>
              )}

              <div>
                <div>Deployment scope</div>
                <div style={{ display: 'grid', gap: '8px', marginTop: '8px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input type="radio" checked={targetScope === 'tenant'} onChange={() => setTargetScope('tenant')} disabled={installing} />
                    <span>Tenant app catalog</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input type="radio" checked={targetScope === 'sitecollection'} onChange={() => setTargetScope('sitecollection')} disabled={installing} />
                    <span>Site collection app catalog</span>
                  </label>
                </div>
              </div>

              {targetScope === 'sitecollection' && (
                <div className="pill warning">
                  The target site must already have a site collection app catalog enabled.
                </div>
              )}

              {targetScope === 'tenant' && selectedReleaseSupportsTeams && (
                <label>
                  Teams organisation app
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginTop: '8px', flexWrap: 'wrap' }}>
                    <input
                      type="checkbox"
                      checked={syncToTeams}
                      onChange={(event) => setSyncToTeams(event.target.checked)}
                      disabled={installing}
                    />
                    <span>Also sync this release to the tenant Teams app catalog</span>
                  </div>
                  <div className="muted" style={{ marginTop: '6px' }}>
                    Teams sync is optional. SharePoint tenant app catalog deployment is still the prerequisite, so there is no separate Teams-only deployment path in this flow.
                  </div>
                </label>
              )}

              {targetScope === 'tenant' ? (
                <div>
                  <div>Target</div>
                  <div style={{ marginTop: '6px', border: '1px solid #e2e8f0', borderRadius: '4px', padding: '10px 12px', background: '#f8fafc' }}>
                    {tenantDefaultTargetUrl || 'No SharePoint host is known yet for this tenant.'}
                  </div>
                  <div className="muted" style={{ marginTop: '6px' }}>
                    {tenantDefaultTargetUrl
                      ? `Deploying to the tenant app catalog resolved from ${tenantDefaultTargetUrl}.`
                      : installTargetHint}
                  </div>
                </div>
              ) : (
                <label>
                  Target
                  {suggestedTargets.length > 0 ? (
                    <select
                      className="input"
                      value={suggestedTargets.some((entry) => entry.url === targetUrl) ? targetUrl : ''}
                      onChange={(event) => {
                        setTargetUrl(event.target.value);
                        setShowAdvanced(false);
                      }}
                      style={{ marginTop: '6px' }}
                      disabled={installing}
                    >
                      <option value="">Choose a verified site collection app catalog…</option>
                      {suggestedTargets.map((entry) => (
                        <option key={`${entry.source}:${entry.url}`} value={entry.url}>
                          {entry.label} — {describeInstallTargetSource(entry.source)}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="badge-subtle" style={{ marginTop: '6px', display: 'inline-flex' }}>
                      {siteCollectionCandidateCount > 0
                        ? 'No verified site collection app catalog sites were found yet.'
                        : 'No discovered targets yet.'}
                    </div>
                  )}
                  <div className="muted" style={{ marginTop: '6px' }}>{installTargetHint}</div>
                </label>
              )}

              {targetScope === 'sitecollection' && graphDiscovery?.available && (
                <div style={{ border: '1px solid #bfdbfe', borderRadius: '4px', padding: '14px', background: '#f7fbff', display: 'grid', gap: '10px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <div>
                      <strong>Optional Microsoft 365 site discovery</strong>
                      <p className="muted" style={{ margin: '6px 0 0' }}>
                        Off by default. Known Edinshall sites and manual URL entry work without Microsoft Graph. If activated, site search uses delegated <code>Sites.Read.All</code> for this tenant; it does not grant Edinshall write access.
                      </p>
                    </div>
                    <button
                      className={graphDiscovery.active ? 'btn secondary' : 'btn'}
                      type="button"
                      disabled={graphDiscoveryUpdating || installing}
                      onClick={() => void setGraphDiscoveryActive(!graphDiscovery.active)}
                    >
                      {graphDiscoveryUpdating
                        ? 'Saving…'
                        : graphDiscovery.active
                          ? 'Turn off discovery'
                          : 'Activate discovery'}
                    </button>
                  </div>

                  {graphDiscovery.active && (
                    <>
                      <div className="muted">
                        Edinshall checks silently for an existing permission first. If it is unavailable, you can continue to Microsoft's permission screen. Your organisation's consent policy may require an IT administrator to approve the request.
                      </div>
                      <label>
                        Search SharePoint sites by name
                        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '6px' }}>
                          <input
                            className="input"
                            value={siteSearch}
                            onChange={(event) => {
                              setSiteSearch(event.target.value);
                              setGraphPermissionRequired(false);
                            }}
                            placeholder="For example: Quality"
                            disabled={installing || searchingSites}
                            style={{ flex: '1 1 280px' }}
                          />
                          <button
                            className="btn secondary"
                            type="button"
                            onClick={() => void runGraphSiteSearch(false)}
                            disabled={installing || searchingSites || siteSearch.trim().length < 2}
                          >
                            {searchingSites ? 'Searching…' : 'Search sites'}
                          </button>
                        </div>
                      </label>
                    </>
                  )}

                  {graphPermissionRequired && graphDiscovery.active && (
                    <div className="pill warning" style={{ display: 'grid', gap: '8px' }}>
                      <strong>Microsoft permission is needed</strong>
                      <span>
                        We could not obtain <code>Sites.Read.All</code> silently. This delegated permission lets the signed-in administrator search and read sites across the selected tenant on their own behalf. Your IT team may need to approve it under your organisation's consent policy.
                      </span>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <button className="btn" type="button" onClick={() => void runGraphSiteSearch(true)} disabled={searchingSites}>
                          Continue to Microsoft permission request
                        </button>
                        <button className="btn secondary" type="button" onClick={() => void copyGraphPermissionRequest()}>
                          {graphItRequestCopied ? 'IT request copied' : 'Copy request for IT'}
                        </button>
                        <button className="btn secondary" type="button" onClick={() => setGraphPermissionRequired(false)}>
                          Not now
                        </button>
                      </div>
                    </div>
                  )}

                  {graphPermissionError && <div className="pill warning">{graphPermissionError}</div>}

                  {targetDiscovery?.searchedSites?.length ? (
                    <div style={{ display: 'grid', gap: '8px' }}>
                      <strong>Microsoft 365 search results</strong>
                      {targetDiscovery.searchedSites.map(entry => (
                        <button
                          key={`graph-search:${entry.url}`}
                          type="button"
                          className="list-row"
                          onClick={() => setTargetUrl(entry.url)}
                          disabled={installing}
                        >
                          <span>{entry.label}<span className="muted" style={{ display: 'block' }}>{entry.url}</span></span>
                          <span className="badge-subtle">
                            {entry.siteCollectionAppCatalogStatus === 'enabled'
                              ? 'App catalog verified'
                              : entry.siteCollectionAppCatalogStatus === 'not_enabled'
                                ? 'App catalog not enabled'
                                : 'App catalog not verified'}
                          </span>
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
              )}

              {targetDiscoveryError && <div className="pill warning">{targetDiscoveryError}</div>}

              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                <button className="btn" type="button" onClick={() => void install()} disabled={installing || !selectedTenantId || !effectiveDeployTargetUrl}>
                  {installing ? 'Deploying…' : selectedReleaseIsRollbackCandidate ? 'Deploy approved rollback release' : 'Deploy'}
                </button>
                <button className="btn secondary" type="button" onClick={() => setShowAdvanced((current) => !current)} disabled={installing}>
                  {showAdvanced ? 'Hide advanced options' : 'Show advanced options'}
                </button>
              </div>

              {showAdvanced && (
                <div style={{ border: '1px solid #e2e8f0', borderRadius: '4px', padding: '12px', background: '#f8fafc', display: 'grid', gap: '12px' }}>
                  <label>
                    {installTargetLabel} (manual override)
                    <input
                      className="input"
                      value={targetUrl}
                      onChange={(event) => setTargetUrl(event.target.value)}
                      placeholder="https://contoso.sharepoint.com/sites/quality"
                      style={{ marginTop: '6px' }}
                      disabled={installing}
                    />
                    <div className="muted" style={{ marginTop: '6px' }}>{installTargetHint}</div>
                  </label>

                  {targetScope === 'sitecollection' && (
                    <p className="muted" style={{ margin: 0 }}>
                      Choose a reported or optionally discovered Edinshall site above, or enter the exact SharePoint site URL here.
                    </p>
                  )}
                </div>
              )}

              {installResult && !activeOperation && (
                <div className="badge-subtle">
                  Deployment started. Operation {installResult.operationId} is being tracked in Deployment History.
                </div>
              )}

              {installError && <div className="pill warning">{installError}</div>}
            </div>
          </div>

          {instanceSummary && (
            <div className="card">
              <h3>Instance Rollout Status</h3>
              <p className="muted">
                App catalog deployment updates the tenant package. Existing site instances may still need install, upgrade, or repair attention.
                Each entry is one web part placement (a page); a site with the web part on two pages, or re-added to a page, shows more than one.
                The loaded version is whatever the site last reported at page load, so it only changes after someone opens the page on the new package.
              </p>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '12px' }}>
                <span className="pill success">{instanceSummary.totalActive} active instances</span>
                <span className="pill warning">{instanceSummary.needsUpdate} need upgrade</span>
                <span className="pill warning">{instanceSummary.needsAttention} need attention</span>
                <span className="pill success">{instanceSummary.maintenanceRunning} maintenance running</span>
                {(instanceSummary.totalSuperseded ?? 0) > 0 && (
                  <span className="badge-subtle">{instanceSummary.totalSuperseded} superseded</span>
                )}
              </div>
              {instanceCurrentVersion && (
                <div className="muted" style={{ marginTop: '8px', fontSize: 12 }}>
                  Upgrade comparisons use v{instanceCurrentVersion.version}
                  {instanceCurrentVersion.source === 'release-index'
                    ? ' from the release archive (recommended release, else the highest published version).'
                    : ' from the configured fallback because the release archive could not be read.'}
                </div>
              )}
              {instanceRows.length > 0 ? (
                <div style={{ display: 'grid', gap: '10px', marginTop: '14px' }}>
                  {groupReleaseInstancesBySite(instanceRows).map((group) => (
                    <div key={group.siteUrl} style={{ border: '1px solid #e2e8f0', borderRadius: '4px', padding: '12px', background: '#f8fafc' }}>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
                        <strong>{group.siteUrl}</strong>
                        {group.rows.length > 1 && (
                          <span className="badge-subtle">{group.rows.length} web part placements</span>
                        )}
                      </div>
                      <div style={{ display: 'grid', gap: '8px', marginTop: '8px' }}>
                        {group.rows.map((instance) => (
                          <div key={instance.instanceId} style={{ borderTop: '1px solid #e2e8f0', paddingTop: '8px' }}>
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
                              <span>
                                <span title={instance.pageUrl || undefined}>{describePagePath(instance.pageUrl, instance.siteUrl)}</span>
                                <span className="muted">{` • v${instance.webPartVersion || 'unknown'} • last seen ${relativeTimeLabel(instance.lastSeenAt)} • ${RELEASE_INSTANCE_STATUS_LABEL[instance.status] ?? instance.status}`}</span>
                              </span>
                              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                {instance.isOutdated && <span className="pill warning">upgrade needed</span>}
                                {instance.attention && <span className="pill warning">{instance.attention.runtimeGate}</span>}
                                {instance.maintenance?.status === 'running' && <span className="pill success">maintenance running</span>}
                                {instance.maintenance?.status === 'pending' && <span className="badge-subtle">maintenance queued</span>}
                                {instance.maintenance?.status === 'failed' && <span className="pill danger">maintenance failed</span>}
                              </div>
                            </div>
                            {(instance.attention?.reason || instance.maintenance?.statusMessage) && (
                              <div className="muted" style={{ marginTop: '4px' }}>
                                {instance.attention?.reason ? instance.attention.reason : ''}
                                {instance.attention?.reason && instance.maintenance?.statusMessage ? ' • ' : ''}
                                {instance.maintenance?.statusMessage ? instance.maintenance.statusMessage : ''}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="badge-subtle" style={{ marginTop: '14px', display: 'inline-flex' }}>
                  No current instance upgrade or repair attention for this tenant.
                </div>
              )}
            </div>
          )}

          {(activeOperation || operations.length > 0) && (
            <div className="card">
              <h3>Deployment History</h3>
              <p className="muted">Recent delegated app catalog deployment attempts across your deployable tenants.</p>

              {activeOperation && (
                <div style={{ marginTop: '14px', border: '1px solid #bfdbfe', borderRadius: '4px', padding: '14px', background: '#f7fbff' }}>
                  <div className={activeOperation.status === 'failed' ? 'pill warning' : activeOperation.status === 'succeeded' ? 'pill success' : 'badge-subtle'}>
                    {activeOperation.status === 'failed' ? 'Deployment failed' : activeOperation.status === 'succeeded' ? 'Deployment succeeded' : 'Deployment in progress'}
                  </div>

                  <div style={{ marginTop: '12px', display: 'grid', gap: '8px' }}>
                    <div><strong>Tenant:</strong> {tenantLookup.get(activeOperation.tenantId)?.displayName || activeOperation.tenantId}</div>
                    <div><strong>Release:</strong> {activeOperation.releaseVersion || activeOperation.releaseTag}</div>
                    <div><strong>Current stage:</strong> {describeStage(activeOperation.stage)}</div>
                    {activeOperation.statusMessage && <div><strong>Current activity:</strong> {activeOperation.statusMessage}</div>}
                    <div><strong>Elapsed:</strong> {formatElapsed(activeOperation.createdAt, now)}</div>
                    <div><strong>Last update:</strong> {formatDateTime(activeOperation.updatedAt)}</div>
                    <div><strong>Polling:</strong> {activeOperation.status === 'running' ? 'Every 3 seconds' : 'Stopped'}</div>
                  </div>

                  <div style={{ marginTop: '14px' }}>
                    <div style={{ height: '10px', borderRadius: '999px', background: '#dbe7f3', overflow: 'hidden' }}>
                      <div
                        style={{
                          width: `${activeProgressPercent}%`,
                          height: '100%',
                          background: activeOperation.status === 'failed' ? '#c2410c' : activeOperation.status === 'succeeded' ? '#15803d' : '#2563eb',
                          transition: 'width 240ms ease'
                        }}
                      />
                    </div>
                    <div style={{ marginTop: '8px', display: 'grid', gap: '4px' }}>
                      {stageOrder.map((stage) => {
                        const currentIndex = stageOrder.indexOf(activeOperation.stage);
                        const stageIndex = stageOrder.indexOf(stage);
                        const isDone = stageIndex < currentIndex || activeOperation.status === 'succeeded';
                        const isCurrent = stage === activeOperation.stage && activeOperation.status === 'running';
                        const label =
                          activeOperation.status === 'failed' && stage === activeOperation.stage
                            ? `${describeStage(stage)} (failed here)`
                            : isCurrent
                              ? `${describeStage(stage)} (current)`
                              : describeStage(stage);
                        return (
                          <div key={stage} className={isCurrent ? 'pill warning' : isDone ? 'pill success' : 'badge-subtle'}>
                            {label}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {activeOperation.appCatalogUrl && <div style={{ marginTop: '12px' }}><strong>App catalog:</strong> {activeOperation.appCatalogUrl}</div>}
                  {activeOperation.appId && <div style={{ marginTop: '8px' }}><strong>App ID:</strong> <code>{activeOperation.appId}</code></div>}
                  {activeOperation.correlationId && <div style={{ marginTop: '8px' }}><strong>Correlation:</strong> <code>{activeOperation.correlationId}</code></div>}
                  {activeOperation.status === 'succeeded' && <div className="pill success" style={{ marginTop: '12px' }}>SharePoint confirmed deployment and post-deploy validation passed.</div>}
                  {activeOperation.lastError && <div className="pill warning" style={{ marginTop: '12px' }}>{activeOperation.lastError}</div>}
                </div>
              )}

              <div style={{ marginTop: '14px', display: 'grid', gap: '10px' }}>
                {operations.map((operation) => {
                  const expanded = Boolean(expandedOperations[operation.operationId]);
                  const tenantEntry = tenantLookup.get(operation.tenantId);
                  return (
                    <div key={operation.operationId}>
                      <button type="button" className="list-row" onClick={() => toggleOperation(operation.operationId)} aria-expanded={expanded}>
                        <span>{operation.releaseVersion || operation.releaseTag} → {tenantEntry?.sharePointHost || operation.targetUrl}</span>
                        <span className={operation.status === 'failed' ? 'pill warning' : operation.status === 'succeeded' ? 'pill success' : 'badge-subtle'}>
                          {operation.status === 'failed' ? 'Failed' : operation.status === 'succeeded' ? 'Succeeded' : 'Running'}
                        </span>
                      </button>

                      {expanded && (
                        <div style={{ border: '1px solid #e2e8f0', borderTop: 'none', borderRadius: '0 0 4px 4px', padding: '12px', background: '#ffffff' }}>
                          <div style={{ display: 'grid', gap: '6px' }}>
                            <div><strong>Tenant:</strong> {tenantEntry?.displayName || operation.tenantId}</div>
                            <div><strong>Scope:</strong> {describeScope(operation.targetScope)}</div>
                            <div><strong>Stage:</strong> {describeStage(operation.stage)}</div>
                            {operation.statusMessage && <div><strong>Status:</strong> {operation.statusMessage}</div>}
                            <div><strong>Target:</strong> {operation.targetUrl}</div>
                            {operation.appCatalogUrl && <div><strong>App catalog:</strong> {operation.appCatalogUrl}</div>}
                            {operation.appId && <div><strong>App ID:</strong> <code>{operation.appId}</code></div>}
                            {operation.correlationId && <div><strong>Correlation:</strong> <code>{operation.correlationId}</code></div>}
                            <div><strong>Started:</strong> {formatDateTime(operation.createdAt)}</div>
                            <div><strong>Updated:</strong> {formatDateTime(operation.updatedAt)}</div>
                            {operation.completedAt && <div><strong>Completed:</strong> {formatDateTime(operation.completedAt)}</div>}
                            {operation.lastError && <div><strong>Error:</strong> {operation.lastError}</div>}
                            {operation.status !== 'running' && (
                              <div style={{ marginTop: '8px' }}>
                                <button
                                  className="btn secondary"
                                  type="button"
                                  onClick={() => { void dismissOperation(operation.operationId); }}
                                  disabled={dismissingOperationId === operation.operationId}
                                >
                                  {dismissingOperationId === operation.operationId ? 'Dismissing…' : 'Dismiss from history'}
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="card">
            <h3>Release Archive</h3>
            <div style={{ display: 'grid', gap: '12px', marginTop: '14px' }}>
              <label>
                Release
                <select className="input" value={selectedTag} onChange={(event) => setSelectedTag(event.target.value)} style={{ marginTop: '6px' }}>
                  {index.releases.map((release) => (
                    <option key={release.releaseTag} value={release.releaseTag}>
                      {release.version}{release.isRecommended ? ' (recommended)' : ''} — {release.releaseChannel}
                    </option>
                  ))}
                </select>
              </label>

              <div>
                <strong>{selectedRelease.releaseName}</strong>
                <div className="muted" style={{ marginTop: '6px' }}>
                  {selectedRelease.version}
                  {' · '}
                  {selectedRelease.releaseChannel}
                  {' · '}
                  Published {formatDateTime(selectedRelease.publishedAtUtc || selectedRelease.generatedAtUtc)}
                  {selectedRelease.isRecommended ? ' · Recommended' : ''}
                </div>
                <div className="muted" style={{ marginTop: '6px' }}>
                  Checksum <code>{selectedRelease.checksumSha256}</code>
                  {selectedRelease.supportsTeamsChoices && selectedRelease.supportsTeamsChoices.length > 0
                    ? ` · Teams ${selectedRelease.supportsTeamsChoices.map(describeTeamsChoice).join(', ')}`
                    : ''}
                  {describeTeamsPackagingMode(selectedRelease.teamsPackagingMode)
                    ? ` · ${describeTeamsPackagingMode(selectedRelease.teamsPackagingMode)}`
                    : ''}
                </div>
              </div>

              <div>
                <a className="btn secondary" href={`/api/admin/spfx-releases/${encodeURIComponent(selectedRelease.releaseTag)}/download`}>
                  Download .sppkg
                </a>
              </div>

              <div className="muted">
                <a href={SHAREPOINT_ALM_DOCS_URL} target="_blank" rel="noreferrer">Microsoft ALM API docs</a>
                {' · '}
                <a href={SHAREPOINT_SITE_COLLECTION_APP_CATALOG_DOCS_URL} target="_blank" rel="noreferrer">Site collection app catalog docs</a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SpfxReleasesAdmin;


